import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { EVENT_OCCURRED, delay, querySelector, renderMessage, waitForEvent } from './dom-util';
import { flowInstance } from './flow';
import { getTabSelector } from './ide-utils';
const TRAIL_FABRIC_FLOW_ID = `TRAIL_FABRIC_FLOW_ID`;
function isTrailFabricFlowEnabled(params: URLSearchParams) {
  const onboardingStr = params.get('onboarding');
  return Boolean(onboardingStr);
}
export function useTrailFabricOnboarding(isUpdateFabric: boolean) {
  const [params] = useSearchParams();

  const enabled = isUpdateFabric ? false : isTrailFabricFlowEnabled(params);
  const navigate = useNavigate();
  const creationRef = useRef(false);
  useEffect(() => {
    if (!creationRef.current) {
      flowInstance.createFlow({
        id: TRAIL_FABRIC_FLOW_ID,
        enabled: enabled,
        steps: [
          {
            id: 'fabric_details',
            startOn: async (step) => {
              await step.operations.waitForElement('input[placeholder="Enter fabric name"]');
            },
            run: async (step) => {
              step.operations.addToast({
                content: renderMessage(
                  <>
                    👉 Provide a Data Fabric <b>name</b> and <b>Continue</b>
                  </>
                )
              });
              await step.operations.showIndicator({
                selector: ['button:not([disabled])', 'Continue']
              });
            }
          },
          {
            id: 'providers',
            run: async (step) => {
              async function task() {
                const isBasicTabActive = Boolean(querySelector(getTabSelector('Basic Info', true)));
                const isProviderTabActive = Boolean(querySelector(getTabSelector('Providers', true)));
                if (isBasicTabActive) {
                  step.operations.addToast({
                    content: renderMessage(
                      <>
                        👉 <b>Click</b> on <b>Continue</b> to finish fabric creation.
                      </>
                    )
                  });
                  await step.operations.showIndicator({
                    selector: ['button:not([disabled])', 'Continue']
                  });
                  await delay(100);
                  await task();
                  return;
                } else if (isProviderTabActive) {
                  step.operations.addToast({
                    content: renderMessage(
                      <>👉 Choose your data provider for your Fabric.</>,
                      <>Use Prophecy managed cluster or your own environment.</>
                    )
                  });
                  step.operations.showIndicator({
                    selector: ['button:not([disabled])', 'Continue'],
                    waitForEvent: false
                  });
                  const result = await Promise.race([
                    step.operations.waitForElement(['span[data-component="dialog.title"]', 'Your fabric is ready!']),
                    step.operations.asyncTask(() =>
                      waitForEvent([querySelector(['button:not([disabled])', 'Back']) as Element], 'click')
                    )
                  ]);
                  if (result === EVENT_OCCURRED) {
                    await task();
                  }
                  return;
                }
              }
              await delay(100);
              await task();
            }
          }
        ]
      });
      if (enabled) {
        flowInstance.startFlow(TRAIL_FABRIC_FLOW_ID);
      }
      creationRef.current = true;
      return () => {
        flowInstance.stopFlow(TRAIL_FABRIC_FLOW_ID);
      };
    }
  }, [enabled, navigate]);
  return { enabled };
}
